import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  InfiniteData,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  ringCentralService,
  ConversationListItem,
  InboxPage,
  InboxCounts,
  InboxRangeParams,
  InboxTab,
  InboxDirection,
  Message,
} from '../../services/ringcentral.service';

/**
 * Communication Center v2 data layer (perf rebuild Phase 5).
 *
 * The conversation LIST is an infinite query over the projection-backed
 * /api/ringcentral/v2/conversations endpoint (50 rows/page, keyset cursor).
 * All filtering (tab, department tag, date range, search) is server-side —
 * the page never loads more than it shows.
 */

export interface InboxFilters {
  tab: InboxTab;
  departmentTag?: string;
  range: InboxRangeParams;
  search?: string;
  /** PRX-132 */
  direction?: InboxDirection;
  /** PRX-132 */
  threadTag?: string;
}

/** PRX-132 — the In / Out and tag filters, shared by the list and the counts. */
export interface InboxExtraFilters {
  direction?: InboxDirection;
  threadTag?: string;
}

const extraKey = (extra?: InboxExtraFilters): string[] =>
  extra?.direction || extra?.threadTag ? [extra.direction || '', extra.threadTag || ''] : [];

const rangeKey = (range: InboxRangeParams): string =>
  range.from && range.to ? `${range.from}|${range.to}` : `d${range.days ?? 14}`;

export const inboxQueryKey = (pharmacyId: string, filters: InboxFilters) => [
  'rc-inbox',
  pharmacyId,
  filters.tab,
  filters.departmentTag || '',
  rangeKey(filters.range),
  filters.search || '',
  ...extraKey(filters),
];

export const inboxCountsQueryKey = (
  pharmacyId: string,
  departmentTag: string | undefined,
  range: InboxRangeParams,
  extra?: InboxExtraFilters,
) => ['rc-inbox-counts', pharmacyId, departmentTag || '', rangeKey(range), ...extraKey(extra)];

export function useConversationInbox(pharmacyId: string | undefined, filters: InboxFilters) {
  return useInfiniteQuery<InboxPage>({
    queryKey: pharmacyId ? inboxQueryKey(pharmacyId, filters) : ['rc-inbox', 'disabled'],
    queryFn: ({ pageParam }) =>
      ringCentralService.getInbox({
        limit: 50,
        cursor: (pageParam as string) || undefined,
        tab: filters.tab,
        departmentTag: filters.departmentTag,
        search: filters.search,
        range: filters.range,
        direction: filters.direction,
        threadTag: filters.threadTag,
      }),
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: !!pharmacyId,
    staleTime: 30_000,
    // Poll fallback for the socket channel. This is NOT belt-and-braces: a
    // patient reply that misses the socket was invisible for two full minutes,
    // and staff read "nothing on screen" as "no reply came in". One minute is
    // the agreed floor for noticing a new message; the socket still delivers
    // the instant case. TanStack refetches ALL loaded pages on interval, so
    // this stays foreground-only — useInboxCounts carries the background badge.
    refetchInterval: 60_000,
    // Overrides the app-wide refetchOnWindowFocus: false (QueryProvider). Staff
    // leave this tab constantly — to the phone, to Teams — and came back to a
    // list frozen at whatever it showed when they left.
    refetchOnWindowFocus: true,
  });
}

export function useInboxCounts(
  pharmacyId: string | undefined,
  departmentTag: string | undefined,
  range: InboxRangeParams,
  extra?: InboxExtraFilters,
) {
  return useQuery<InboxCounts>({
    queryKey: pharmacyId
      ? inboxCountsQueryKey(pharmacyId, departmentTag, range, extra)
      : ['rc-inbox-counts', 'disabled'],
    queryFn: () =>
      ringCentralService.getInboxCounts({
        departmentTag,
        range,
        direction: extra?.direction,
        threadTag: extra?.threadTag,
      }),
    enabled: !!pharmacyId,
    staleTime: 30_000,
    // The Unread / Needs Review badges are the ONLY signal that something
    // arrived while staff were looking elsewhere, so this one keeps polling
    // when the tab is in the background. Without refetchIntervalInBackground
    // TanStack pauses the timer on blur, and an alt-tabbed user came back to
    // counts frozen at whatever they were when they left.
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
    // Same override as the list: the badge is the signal staff look for the
    // instant they return to the tab, so it must not be a stale cache read.
    refetchOnWindowFocus: true,
  });
}

/** Map a list-row's preview into the legacy Message shape ConversationList renders. */
export function listItemToPreviewMessage(item: ConversationListItem): Message | null {
  if (!item.lastMessage?.id) return null;
  return {
    id: item.lastMessage.id,
    type: 'SMS',
    direction: (item.lastMessage.direction?.toUpperCase() as Message['direction']) || 'INBOUND',
    from: item.lastMessage.direction === 'outbound' ? '' : item.phoneNumber,
    to: item.lastMessage.direction === 'outbound' ? item.phoneNumber : '',
    text: item.lastMessage.preview ?? '',
    // The row-level unread badge comes from item.unreadCount; mark the preview
    // message read/unread consistently so legacy per-message scans agree.
    status: item.unreadCount > 0 && item.lastMessage.direction === 'inbound'
      ? (item.lastMessage.status === 'read' ? 'received' : item.lastMessage.status ?? 'received')
      : item.lastMessage.status ?? 'sent',
    createdAt: item.lastMessageAt,
    updatedAt: item.lastMessageAt,
    createdByName: item.lastMessage.createdByName ?? undefined,
  };
}

/**
 * Cache-patching helpers shared by the socket sync hook and optimistic
 * mutations. They patch EVERY cached rc-inbox query for the pharmacy (each
 * tab/filter combination has its own key).
 */
export function useInboxCachePatcher(pharmacyId: string | undefined) {
  const queryClient = useQueryClient();

  /** Replace-or-prepend a row; rows always re-sort to the top (new activity). */
  const upsertRow = useCallback(
    (row: ConversationListItem) => {
      if (!pharmacyId) return;
      const queries = queryClient.getQueriesData<InfiniteData<InboxPage>>({
        queryKey: ['rc-inbox', pharmacyId],
      });
      for (const [key, data] of queries) {
        if (!data?.pages?.length) continue;
        const without = data.pages.map((page) => ({
          ...page,
          items: page.items.filter((i) => i.phoneNumber !== row.phoneNumber),
        }));
        // PRX-132: a staff-archived thread belongs only in the Archived tab
        // (key[2] is the tab — see inboxQueryKey).
        const belongsHere = !row.archivedAt || key[2] === 'archived';
        if (belongsHere) without[0] = { ...without[0], items: [row, ...without[0].items] };
        queryClient.setQueryData(key, { ...data, pages: without });
      }
    },
    [pharmacyId, queryClient],
  );

  /** Patch fields on an existing row wherever it appears (no reordering). */
  const patchRow = useCallback(
    (phoneNumber: string, patch: Partial<ConversationListItem>) => {
      if (!pharmacyId) return;
      const queries = queryClient.getQueriesData<InfiniteData<InboxPage>>({
        queryKey: ['rc-inbox', pharmacyId],
      });
      for (const [key, data] of queries) {
        if (!data?.pages?.length) continue;
        let changed = false;
        const pages = data.pages.map((page) => {
          const idx = page.items.findIndex((i) => i.phoneNumber === phoneNumber);
          if (idx < 0) return page;
          changed = true;
          const items = [...page.items];
          items[idx] = { ...items[idx], ...patch };
          return { ...page, items };
        });
        if (changed) queryClient.setQueryData(key, { ...data, pages });
      }
    },
    [pharmacyId, queryClient],
  );

  const invalidateInbox = useCallback(() => {
    if (!pharmacyId) return;
    queryClient.invalidateQueries({ queryKey: ['rc-inbox', pharmacyId] });
  }, [pharmacyId, queryClient]);

  const invalidateCounts = useCallback(() => {
    if (!pharmacyId) return;
    queryClient.invalidateQueries({ queryKey: ['rc-inbox-counts', pharmacyId] });
  }, [pharmacyId, queryClient]);

  return { upsertRow, patchRow, invalidateInbox, invalidateCounts };
}
